import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

/** Modal para ver un recibo de sueldo (imagen o PDF), mismo patrón que DNI. */
export function PayslipFileModal({
  doc,
  onClose,
}: {
  doc: { id: string; originalName?: string; mimeType?: string };
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    apiService
      .getOriginationDocumentFileBlob(doc.id)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setUrl(objectUrl);
          setLoading(false);
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(e?.response?.data?.message ?? e?.message ?? 'No se pudo cargar el archivo');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id]);

  const name = doc.originalName ?? 'Recibo';
  const mime = doc.mimeType ?? '';
  const isImage = mime.startsWith('image/');
  const isPdf =
    mime === 'application/pdf' || /\.pdf$/i.test(doc.originalName ?? '');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Vista del recibo de sueldo"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate pr-4">
            Recibo · {name}
          </h2>
          <div className="flex items-center gap-3 shrink-0">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-700 hover:underline"
              >
                Abrir en pestaña
              </a>
            )}
            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-sm">
              Cerrar
            </button>
          </div>
        </div>
        <div className="p-4 overflow-auto flex-1 bg-gray-50">
          {loading && <p className="text-sm text-gray-500 text-center py-16">Cargando…</p>}
          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>
          )}
          {!loading && !error && url && isImage && (
            <img src={url} alt={name} className="max-h-[75vh] w-full object-contain rounded" />
          )}
          {!loading && !error && url && isPdf && (
            <iframe
              src={url}
              title={name}
              className="w-full h-[75vh] rounded border bg-white"
            />
          )}
          {!loading && !error && url && !isImage && !isPdf && (
            <div className="text-center py-16 space-y-2">
              <p className="text-sm text-gray-600">Vista previa no disponible para este tipo de archivo.</p>
              <a href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 hover:underline">
                Descargar / abrir {name}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
